import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FeedbackLink — drop-in trigger + dialog. Renders as a plain link by default.
 *
 * Props:
 *  - className: extra classes on the trigger
 *  - label:    override link text (defaults to "Send feedback")
 *  - variant:  "link" (default) | "muted" (footer style)
 */
export default function FeedbackLink({ className = "", label = "Send feedback", variant = "link" }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 3) {
      toast.error("Please share a little more so we can act on it.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        message: trimmed,
        contact_email: email.trim() || undefined,
        path: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      toast.success("Thanks — your feedback was sent anonymously.");
      setMessage("");
      setEmail("");
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const triggerClass =
    variant === "muted"
      ? `hover:text-stone-800 cursor-pointer ${className}`
      : `text-amber-700 hover:text-amber-900 underline underline-offset-2 cursor-pointer ${className}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
        data-testid="feedback-open-link"
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-500" />
              Send anonymous feedback
            </DialogTitle>
            <DialogDescription>
              Bugs, requests, or just a thought — it all helps. Your submission is anonymous unless you leave an email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="fb-message">What&apos;s on your mind?</Label>
              <Textarea
                id="fb-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. It would be great if the schedule could color-code by subject…"
                rows={6}
                className="mt-1.5 resize-none"
                data-testid="feedback-message-input"
                autoFocus
              />
              <div className="mt-1 text-[11px] text-stone-400 text-right">{message.length}/5000</div>
            </div>

            <div>
              <Label htmlFor="fb-email" className="flex items-center gap-1.5">
                Email <span className="text-stone-400 text-xs font-normal">(optional — only if you want a reply)</span>
              </Label>
              <Input
                id="fb-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                data-testid="feedback-email-input"
              />
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-stone-50 border border-stone-200 px-3 py-2.5 text-xs text-stone-600">
              <ShieldCheck className="w-4 h-4 text-emerald-500 flex-none mt-0.5" />
              <span>We don&apos;t track who you are. Only the message, the page you were on, and browser info are stored.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="feedback-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="feedback-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-1.5" /> Send feedback</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
